/**
 * Site-Wide Jackpot message handler for MDS opt-in status updates.
 */

import { ddRumError } from '@/utils/datadog';
import { ddErrorMdsHandleMessage } from '@/utils/datadog/site-wide-jackpot';
import type { MdsMessage } from '@/lib/mds/types';
import type { OptInStatusPayload, OptInUpdateCallback } from './swj-types';

/**
 * Creates a message handler for JACKPOT messages.
 *
 * @param jackpotId - The expected jackpot ID to filter messages
 * @param updateOptInStatus - Callback function to update the opt-in status in Zustand store
 * @returns A message handler function to register with MdsClient
 */
export const createSwjMessageHandler = (jackpotId: string, updateOptInStatus: OptInUpdateCallback) => {
    return (message: MdsMessage): void => {
        let parsedPayload: OptInStatusPayload;

        try {
            parsedPayload = JSON.parse(message.payload) as OptInStatusPayload;
        } catch (error) {
            ddRumError(ddErrorMdsHandleMessage(error as Error, { jackpotId }));
            return;
        }

        if (message.subtype === 'OPT_IN_STATUS') {
            // Validate the message is for the correct jackpot
            if (parsedPayload.jackpotId !== jackpotId || parsedPayload.optedIn === undefined) {
                return;
            }

            if (parsedPayload.optedIn) {
                const multiplier = (parsedPayload.multiplier ?? 1) as 0 | 1 | 2;
                updateOptInStatus(multiplier);
            } else {
                updateOptInStatus(0);
            }
        }
    };
};
